import { useEffect, useRef, useState } from "react";
import { Calendar, ChevronLeft, ChevronRight } from "lucide-react";
import { DateWheels } from "./ChangelogDatePicker";

const formatDate = (value: string) => {
  if (!value) return "";
  const [year, month, day] = value.split("-");
  return `${month}/${day}/${year}`;
};
const parseDate = (draft: string): string | null => {
  const match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(draft.trim());
  if (!match) return null;
  const month = Number(match[1]);
  const day = Number(match[2]);
  const year = Number(match[3]);
  const date = new Date(year, month - 1, day);
  if (year < 1000 || date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return null;
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
};

// Posting deadlines/targets and Project award dates share manual entry and an explicit calendar button.
// Manual MM/DD/YYYY entry emits ISO date-only values; invalid drafts emit empty
// and report invalidity so callers block progression rather than silently omit a date.
// Callers retain raw text separately from the ISO value across Review
// collapse and step changes; remounting reports its actual validity, never clears it.
// inlineCalendar keeps the calendar in a modal's scroll flow so overflow cannot clip its controls.
export function PostingDatePicker({ id, calendarLabel, inlineCalendar = false, wheel = false, value, draft, onDraftChange, onChange, onValidityChange }: { id: string; calendarLabel: string; inlineCalendar?: boolean; wheel?: boolean; value: string; draft: string; onDraftChange: (draft: string) => void; onChange: (value: string) => void; onValidityChange: (valid: boolean) => void }) {
  const [open, setOpen] = useState(false);
  const [above, setAbove] = useState(false);
  const [touched, setTouched] = useState(false);
  const [month, setMonth] = useState(() => {
    const date = value ? new Date(`${value}T00:00:00`) : new Date();
    return new Date(date.getFullYear(), date.getMonth(), 1);
  });
  const pickerRef = useRef<HTMLDivElement>(null);
  const calendarRef = useRef<HTMLDivElement>(null);
  const calendarButtonRef = useRef<HTMLButtonElement>(null);

  const dateIsInvalid = Boolean(draft.trim() && !parseDate(draft));
  useEffect(() => { onValidityChange(!dateIsInvalid); }, [dateIsInvalid, onValidityChange]);

  useEffect(() => {
    if (open && inlineCalendar) calendarRef.current?.scrollIntoView({ block: "nearest" });
  }, [open, inlineCalendar]);

  useEffect(() => {
    if (!open) return;
    const closeOutside = (event: MouseEvent) => {
      if (!pickerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", closeOutside);
    return () => document.removeEventListener("mousedown", closeOutside);
  }, [open]);

  const year = month.getFullYear();
  const monthIndex = month.getMonth();
  const firstWeekday = month.getDay();
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
  const days = Array.from({ length: firstWeekday + daysInMonth }, (_, index) => index - firstWeekday + 1);
  const setSelectedDate = (next: string) => {
    onDraftChange(formatDate(next));
    setTouched(false);
    onValidityChange(true);
    onChange(next);
    setOpen(false);
    calendarButtonRef.current?.focus();
  };
  const selectDay = (day: number) => {
    setSelectedDate(`${year}-${String(monthIndex + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`);
  };
  const toggleCalendar = () => {
    if (!open) {
      const date = value ? new Date(`${value}T00:00:00`) : new Date();
      setMonth(new Date(date.getFullYear(), date.getMonth(), 1));
      const field = pickerRef.current?.getBoundingClientRect();
      setAbove(Boolean(!inlineCalendar && field && field.bottom + 320 > window.innerHeight && field.top >= 320));
    }
    setOpen(!open);
  };

  return <div ref={pickerRef} className="relative" onKeyDown={(event) => {
    if (event.key !== "Escape" || !open) return;
    event.preventDefault();
    event.stopPropagation();
    setOpen(false);
    calendarButtonRef.current?.focus();
  }}>
    <div className="relative">
      <input id={id} type="text" className="field pr-12" value={draft} maxLength={10} placeholder="MM/DD/YYYY" aria-invalid={touched && dateIsInvalid} aria-describedby={touched && dateIsInvalid ? `${id}-error` : undefined} onChange={(event) => {
        const next = event.target.value;
        const parsed = parseDate(next);
        onDraftChange(next);
        onValidityChange(!next.trim() || Boolean(parsed));
        onChange(parsed || "");
      }} onBlur={() => setTouched(true)} />
      <button ref={calendarButtonRef} type="button" aria-label={calendarLabel} aria-haspopup={wheel ? "dialog" : undefined} aria-expanded={open} aria-controls={wheel ? undefined : `${id}-calendar`} onClick={toggleCalendar} className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-mut hover:text-navy"><Calendar className="h-4 w-4" aria-hidden="true" /></button>
    </div>
    {touched && dateIsInvalid && <p id={`${id}-error`} className="mt-1 text-xs text-brand-red">Enter a valid date as MM/DD/YYYY.</p>}
    {open && wheel && <DateWheels label={calendarLabel} value={value} onCancel={() => setOpen(false)} onDone={setSelectedDate} />}
    {open && !wheel && <div ref={calendarRef} id={`${id}-calendar`} role="group" aria-label={calendarLabel} className={`z-40 w-full rounded-lg border border-line bg-white p-3 shadow-lift ${inlineCalendar ? "relative mt-2" : `absolute right-0 sm:w-80 ${above ? "bottom-full mb-1" : "mt-1"}`}`}>
      <div className="mb-3 flex items-center justify-between">
        <button type="button" aria-label="Previous month" onClick={() => setMonth(new Date(year, monthIndex - 1, 1))} className="rounded p-1.5 hover:bg-navy-50"><ChevronLeft className="h-4 w-4" /></button>
        <strong className="text-sm text-navy">{new Intl.DateTimeFormat("en-PH", { month: "long", year: "numeric" }).format(month)}</strong>
        <button type="button" aria-label="Next month" onClick={() => setMonth(new Date(year, monthIndex + 1, 1))} className="rounded p-1.5 hover:bg-navy-50"><ChevronRight className="h-4 w-4" /></button>
      </div>
      <div className="grid grid-cols-7 text-center text-xs text-mut">{["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => <span key={day}>{day}</span>)}</div>
      <div className="mt-1 grid grid-cols-7 gap-0.5">{days.map((day, index) => day > 0
        ? <button key={index} type="button" aria-label={new Date(year, monthIndex, day).toLocaleDateString("en-PH", { dateStyle: "long" })} onClick={() => selectDay(day)} className={`h-9 rounded text-sm hover:bg-navy-50 ${value === `${year}-${String(monthIndex + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}` ? "bg-navy text-white hover:bg-navy-600" : "text-navy"}`}>{day}</button>
        : <span key={index} />)}</div>
      <div className="mt-2 flex justify-between border-t border-line pt-2 text-xs">
        <button type="button" onClick={() => { const today = new Date(); setMonth(new Date(today.getFullYear(), today.getMonth(), 1)); setSelectedDate(`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`); }} className="text-navy hover:underline">Today</button>
        <button type="button" onClick={() => setSelectedDate("")} className="text-mut hover:underline">Clear</button>
      </div>
    </div>}
  </div>;
}
